import { alpha, Stack, Table, TableBody, TableCell, TableContainer, TableRow, tableRowClasses, Tooltip, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import {
  enqueueSnackbar,
  formatAmount,
  getFormattedDateString,
  ITableHeadCustomType,
  TableHeadCustom,
  TablePaginationCustom,
  TableSpinner,
  useTable,
  useUserDetailsStore,
} from '@zeta-core';
import { FinancialMisService, getErrorMessage } from '@zeta-services';
import { IEmptyRequestResponse, IGenericErrorResponse } from '@zeta-types';

const HEAD_LABEL: ITableHeadCustomType[] = [
  { label: 'Date', width: 353, show: true, id: 'transactionDate' },
  { label: 'Fund Name', width: 353, show: true, id: 'securityName' },
  { label: 'ISIN', width: 353, show: true, id: 'isin' },
  { label: 'Transaction Type', width: 353, show: true, id: 'transactionType' },
  { label: 'Amount', width: 353, show: true, id: 'amount' },
];

const TransactionsTable = ({ isClient }: { isClient?: boolean }) => {
  const userStore = useUserDetailsStore((state) => state.user);
  const location = useLocation();
  const state = location?.state;
  const clientID = isClient ? userStore?.accessibleEntityID : state?.details?.clientId;
  const theme = useTheme();
  const table = useTable({ defaultRowsPerPage: 5, defaultOrderBy: 'transactionDate' });

  const [transactionData, setTransactionData] = useState();

  const getTransactionData = useMutation({
    mutationFn: (payload: IEmptyRequestResponse) =>
      isClient ? FinancialMisService().getPortfolioTransactions(payload) : FinancialMisService().getPortfolioTransactionsAdvisor(payload),
    onSuccess: (response) => {
      setTransactionData(response?.data);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  useEffect(() => {
    if (clientID) {
      getTransactionData?.mutateAsync({
        clientID,
        accountId: state?.details?.accountId || state?.accountId,
        pageSize: table?.rowsPerPage,
        pageIndex: table?.page + 1,
        sort: table.orderBy,
        desc: table.order === 'desc',
      });
    }
  }, [table?.rowsPerPage, table?.page, clientID, table.order, table.orderBy]);

  return (
    <>
      <TableContainer>
        <Table
          role="grid"
          stickyHeader
          size={table.dense ? 'small' : 'medium'}
          sx={{
            [`& .${tableRowClasses.root}`]: {
              '&:nth-of-type(even)': {
                backgroundColor: alpha(theme.palette.grey[500], 0.08),
              },
              '&:last-child td, &:last-child th': {
                border: 0,
              },
            },
          }}
        >
          <TableHeadCustom headLabel={HEAD_LABEL} order={table.order} orderBy={table.orderBy} onSort={table.onSort} />

          <TableBody>
            {getTransactionData?.isPending && <TableSpinner colSpan={12} />}
            {!transactionData?.transactions?.length && !getTransactionData?.isPending && (
              <TableCell colSpan={12} sx={{ borderLeft: '3px solid #000000 !important' }}>
                <Stack alignItems="center" justifyContent="center" height="15vh">
                  <Typography variant="subtitle2" color="text.secondary">
                    No Transactions Available
                  </Typography>
                </Stack>
              </TableCell>
            )}
            {!getTransactionData?.isPending &&
              transactionData?.transactions?.map((item, index) => {
                return (
                  <TableRow key={index}>
                    <TableCell sx={{ borderLeft: '3px solid #000000 !important' }}>{getFormattedDateString(item?.transactionDate)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
                      <Tooltip title={item?.securityName ?? '--'}>{item?.securityName ?? '--'}</Tooltip>
                    </TableCell>
                    <TableCell>{item?.isin || '--'}</TableCell>
                    <TableCell>{item?.transactionType ?? '--'}</TableCell>
                    <TableCell>{formatAmount(item?.amount)}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePaginationCustom
        page={table.page}
        rowsPerPage={table.rowsPerPage}
        onPageChange={table.onChangePage}
        onChangeDense={table.onChangeDense}
        count={getTransactionData?.data?.data?.totalRecords || 0}
        onRowsPerPageChange={table.onChangeRowsPerPage}
      />
    </>
  );
};

export default TransactionsTable;
